import { api } from '../../api/client';
import type { ComponentBrief } from '../components/api';

export type PurchaseStatus =
  | 'draft'
  | 'ordered'
  | 'shipped'
  | 'partial'
  | 'received'
  | 'cancelled';

/** The order of the life of an order. `partial` and `received` are reached by
 *  receiving, never by picking them, so they are not offered as a choice. */
export const CHOOSABLE_STATUSES: PurchaseStatus[] = ['draft', 'ordered', 'shipped'];

export interface Vendor {
  id: string;
  name: string;
  website: string | null;
  /** What the invoice says, as opposed to what the shop calls itself. */
  legal_name: string | null;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  note: string | null;
}

export type VendorInput = Partial<Omit<Vendor, 'id'>> & { name: string };

/** Where part of a line actually landed — read from the ledger, so it is what
 *  happened rather than what was intended. */
export interface LineReceipt {
  location_id: string | null;
  location_name: string | null;
  location_code: string | null;
  quantity: string;
  occurred_at: string;
  package_ids: string[];
}

export interface PurchaseLine {
  id: string;
  line_no: number;
  component_id: string;
  quantity: string;
  unit_price: string | null;
  received_quantity: string;
  /** Where it was meant to go. Distinct from `receipts`, which is where it went. */
  target_location_id: string | null;
  expected_at: string | null;
  purpose_note: string | null;
  coverage: Array<{ id: string; project_id: string; quantity: string }>;
  /** Values copied into the next receipt. They never rewrite a posted lot. */
  receipt_lot_number: string | null;
  receipt_date_code: string | null;
  receipt_package_type: string | null;
  receipt_tare_weight_g: string | null;
  receipt_lot_note: string | null;
  component: ComponentBrief | null;
  receipts: LineReceipt[];
}

export interface Purchase {
  id: string;
  code: string;
  vendor_id: string | null;
  status: PurchaseStatus;
  ordered_at: string | null;
  expected_at: string | null;
  shipped_at: string | null;
  received_at: string | null;
  currency: string;
  fx_rate: string | null;
  shipping_cost: string | null;
  total_cost: string | null;
  /** The order on the shop's own site: what was bought and for how much, as
   *  opposed to the tracking number, which says where the parcel is. */
  order_url: string | null;
  tracking_number: string | null;
  carrier_id: string | null;
  /** Built by the server from the carrier's template, or the configured one. */
  tracking_url: string | null;
  note: string | null;
  lines: PurchaseLine[];
}

export interface PurchaseInput {
  vendor_id?: string | null;
  currency?: string | null;
  fx_rate?: string | null;
  ordered_at?: string | null;
  expected_at?: string | null;
  order_url?: string | null;
  note?: string | null;
}

export interface PurchaseUpdate extends PurchaseInput {
  status?: PurchaseStatus;
  shipped_at?: string | null;
  received_at?: string | null;
  shipping_cost?: string | null;
  tracking_number?: string | null;
}

export interface PurchaseLineInput {
  component_id: string;
  quantity: string;
  unit_price?: string | null;
  target_location_id?: string | null;
  expected_at?: string | null;
  purpose_note?: string | null;
  receipt_lot_number?: string | null;
  receipt_date_code?: string | null;
  receipt_package_type?: string | null;
  receipt_tare_weight_g?: string | null;
  receipt_lot_note?: string | null;
}

export interface PurchaseLineUpdate {
  quantity?: string;
  unit_price?: string | null;
  target_location_id?: string | null;
  expected_at?: string | null;
  purpose_note?: string | null;
  receipt_lot_number?: string | null;
  receipt_date_code?: string | null;
  receipt_package_type?: string | null;
  receipt_tare_weight_g?: string | null;
  receipt_lot_note?: string | null;
}

export interface ReceiptLine {
  line_id: string;
  quantity: string;
  location_id: string;
  /** One entry per unit, for a part tracked that way. A blank entry means that
   *  unit came without a legible number and takes ours. */
  serials?: string[];
  external_lot_number?: string;
  date_code?: string;
  lot_note?: string;
  lot_id?: string;
  packages?: Array<{
    quantity: string;
    package_type: string;
    tare_weight_g?: string | null;
    availability?: 'released' | 'quarantine' | 'rejected';
    note?: string;
  }>;
}

/** One receiving operation is one physical event. Its date is shared by the
 * lines because a parcel may be put away into several locations at once. */
export interface ReceiveRequest {
  lines: ReceiptLine[];
  /** Local calendar date selected by the operator, sent at noon so its date
   * survives conversion on servers in a different time zone. */
  occurred_at?: string;
}

export const listVendors = () => api.get<Vendor[]>('/reference/vendors');

export const createVendor = (input: VendorInput) =>
  api.post<Vendor>('/reference/vendors', input);

export const updateVendor = (id: string, input: Partial<VendorInput>) =>
  api.patch<Vendor>(`/reference/vendors/${id}`, input);

/** `q` searches the lines as well as the order: "which order did those
 *  connectors come on" is answered by a line, never by a field of the order. */
export const listPurchases = (status?: PurchaseStatus, q?: string) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  const query = params.toString();
  return api.get<Purchase[]>(`/purchases${query ? `?${query}` : ''}`);
};

/** One order seen from a part's card: what was bought, when, at what price. */
export interface ComponentPurchase {
  purchase_id: string;
  code: string;
  status: PurchaseStatus;
  vendor_id: string | null;
  ordered_at: string | null;
  received_at: string | null;
  currency: string;
  line_id: string;
  quantity: string;
  received_quantity: string;
  unit_price: string | null;
}

export const purchasesOfComponent = (componentId: string) =>
  api.get<ComponentPurchase[]>(`/components/${componentId}/purchases`);

export const getPurchase = (id: string) => api.get<Purchase>(`/purchases/${id}`);

export const createPurchase = (input: PurchaseInput) =>
  api.post<Purchase>('/purchases', input);

export interface WantedLine {
  component_id: string;
  quantity: string;
  /** Absent means "no supplier known": those end up together in one order
   *  without a vendor rather than being dropped. */
  vendor_id?: string | null;
  purpose_note?: string | null;
}

/** Raise draft orders for a set of chosen parts, one per supplier — an order
 *  naming parts from four shops cannot be placed anywhere.
 *
 *  Prices are left empty rather than written as zero: a zero would travel into
 *  the receipt as the unit cost and from there into the price history, dragging
 *  every average this part has down with it. */
export const createDrafts = (lines: WantedLine[], note?: string) =>
  api.post<Purchase[]>('/purchases/drafts', { lines, note });

export const updatePurchase = (id: string, input: PurchaseUpdate) =>
  api.patch<Purchase>(`/purchases/${id}`, input);

export const addLine = (purchaseId: string, input: PurchaseLineInput) =>
  api.post<PurchaseLine>(`/purchases/${purchaseId}/lines`, input);

export const updateLine = (purchaseId: string, lineId: string, input: PurchaseLineUpdate) =>
  api.patch<PurchaseLine>(`/purchases/${purchaseId}/lines/${lineId}`, input);

/** Intentional project allocation; a missing allocation stays common stock. */
export const setLineCoverage = (
  purchaseId: string,
  lineId: string,
  allocations: Array<{ project_id: string; quantity: string }>,
) => api.put<PurchaseLine>(`/purchases/${purchaseId}/lines/${lineId}/coverage`, { allocations });

export const deleteLine = (purchaseId: string, lineId: string) =>
  api.delete<void>(`/purchases/${purchaseId}/lines/${lineId}`);

/** Partial receipts are the normal case: a parcel arrives split, or one line is
 *  back-ordered. Only the lines being put away are sent. */
export const receivePurchase = (purchaseId: string, input: ReceiveRequest) =>
  api.post<Purchase>(`/purchases/${purchaseId}/receive`, input);

export const cancelPurchase = (id: string) => api.post<Purchase>(`/purchases/${id}/cancel`);

export const archivePurchase = (id: string) => api.delete<void>(`/purchases/${id}`);
